import React from "react";
import { z } from "zod";

import { ButtonStandard, FormSingleSelect, ModalBase, PYForm, Text, toast } from "@performyard/py-components";
import type { CompetencyAssignmentJSON, CompetencyDictEntry } from "@performyard/service-contracts";

import { useUpdateAssignmentCurrentLevel } from "../../../../api/hooks";

const NOT_ASSESSED = "none";

const formSchema = z.object({ level: z.string() });
type FormValues = z.infer<typeof formSchema>;

type UpdateCurrentLevelModalProps = {
	isOpen: boolean;
	onClose: () => void;
	assignment: CompetencyAssignmentJSON | null;
	competency: CompetencyDictEntry | undefined;
	assignee?: { name: string };
};

const UpdateCurrentLevelModal = ({
	isOpen,
	onClose,
	assignment,
	competency,
	assignee,
}: UpdateCurrentLevelModalProps): React.ReactElement | null => {
	const { mutate, isPending } = useUpdateAssignmentCurrentLevel();

	if (!assignment || !competency) return null;

	const levelOptions = [
		{ value: NOT_ASSESSED, label: "Not assessed" },
		...[...competency.levels]
			.sort((a, b) => a.rank - b.rank)
			.map((l) => ({ value: String(l.rank), label: l.name })),
	];

	const defaultValues: FormValues = {
		level: assignment.current_level === null ? NOT_ASSESSED : String(assignment.current_level),
	};

	const onSubmit = ({ value }: { value: FormValues }): void => {
		const currentLevel = value.level === NOT_ASSESSED ? null : Number(value.level);
		mutate(
			{ assignmentId: assignment._id, currentLevel },
			{
				onSuccess: () => {
					toast.success("Current level updated");
					onClose();
				},
				onError: () => {
					toast.error("Failed to update current level");
				},
			}
		);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[432px] overflow-visible">
			<div className="flex flex-col px-4 pt-4 pb-6">
				<div className="pb-4">
					<div className="flex items-center justify-center">
						<Text weight="semi-bold" size="sub-heading">
							Update Competency Level
						</Text>
					</div>
					<div className="flex items-center justify-center">
						<Text size="paragraph-2" weight="regular">
							<span className="py-line-clamp-1 break-all">
								{competency.name} for {assignee?.name || "Employee"}
							</span>
						</Text>
					</div>
				</div>
				<PYForm
					id="update-current-level-form"
					schema={formSchema}
					defaultValues={defaultValues}
					onSubmit={onSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="space-y-4">
						<FormSingleSelect<string>
							id="level"
							label="Current Level"
							options={levelOptions}
							selectionVariant="select"
							placeholder="Select a level"
						/>
						<div className="flex justify-center gap-2 pt-2">
							<ButtonStandard
								size="large"
								color="neutral"
								variant="outline"
								onClick={onClose}
								disabled={isPending}
								dangerouslySetClassName="w-fit"
							>
								Cancel
							</ButtonStandard>
							<ButtonStandard
								size="large"
								color="primary"
								type="submit"
								disabled={isPending}
								dangerouslySetClassName="w-fit"
							>
								Update Level
							</ButtonStandard>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default UpdateCurrentLevelModal;
